// Baseline ecological scorecard data for the Gundre plots (project a8cb44bf-5ea4-48ee-b3f6-07e067c587fd),
// sourced from Gundre_Plot{1..4}_baseline_scorecard.csv.

export const GUNDRE_SCORECARD_PROJECT_ID = 'a8cb44bf-5ea4-48ee-b3f6-07e067c587fd';

export type ScorecardUnit = 'percent' | 'index' | 'count' | 'temp';

export type ConcernLevel = 'very-low' | 'low' | 'moderate' | 'high' | 'very-high';

export interface ScorecardIndicator {
  name: string;
  value: number | null;
  tier1: number | null;
  tier2: number | null;
  unit: ScorecardUnit;
  decimals: number;
  // Upper bound of this indicator's natural scale, used to size its value bar (raw value, pre-formatting).
  gaugeMax: number;
  // Concern level as sourced from the reference baseline scorecard report; null where no
  // reference concern level is known (not derived/guessed — see CONCERN_OVERRIDES below).
  concern: ConcernLevel | null;
}

export type GundrePlotKey = 'Gundre Plot 1' | 'Gundre Plot 2' | 'Gundre Plot 3' | 'Gundre Plot 4';

export const GUNDRE_PLOT_KEYS: GundrePlotKey[] = [
  'Gundre Plot 1',
  'Gundre Plot 2',
  'Gundre Plot 3',
  'Gundre Plot 4',
];

// Indicator metadata, in the same order every scorecard CSV uses.
// gaugeMax is the raw-value ceiling used to size each indicator's value bar.
const INDICATOR_META: Array<{ name: string; unit: ScorecardUnit; decimals: number; gaugeMax: number }> = [
  { name: 'Natural Habitat Extent', unit: 'percent', decimals: 1, gaugeMax: 100 },
  { name: 'Natural Land Cover Proportion', unit: 'percent', decimals: 1, gaugeMax: 100 },
  { name: 'Landscape Connectivity (CPLAND)', unit: 'percent', decimals: 2, gaugeMax: 100 },
  { name: 'Tree Cover Loss Rate', unit: 'percent', decimals: 2, gaugeMax: 100 },
  { name: 'KBA/IBA Overlap', unit: 'percent', decimals: 1, gaugeMax: 100 },
  { name: 'Vegetation Structure (NDVI)', unit: 'index', decimals: 2, gaugeMax: 1 },
  { name: 'Habitat Health Index (HHI)', unit: 'index', decimals: 2, gaugeMax: 5 },
  { name: 'Forest Landscape Integrity Index', unit: 'index', decimals: 2, gaugeMax: 10 },
  { name: 'Ecosystem Integrity Index', unit: 'index', decimals: 2, gaugeMax: 1 },
  { name: 'EII: Structural Integrity', unit: 'index', decimals: 2, gaugeMax: 1 },
  { name: 'EII: Compositional Integrity', unit: 'index', decimals: 2, gaugeMax: 1 },
  { name: 'EII: Functional Integrity', unit: 'index', decimals: 2, gaugeMax: 1 },
  { name: 'Biodiversity Intactness Index', unit: 'index', decimals: 2, gaugeMax: 1 },
  { name: 'Potentially Disappeared Fraction', unit: 'index', decimals: 2, gaugeMax: 1 },
  { name: 'Aridity Index', unit: 'index', decimals: 2, gaugeMax: 1 },
  { name: 'Leaf Area Index (MODIS MCD15A3H)', unit: 'index', decimals: 2, gaugeMax: 3 },
  { name: 'Canopy Height Model (GEDI L2A rh98)', unit: 'index', decimals: 1, gaugeMax: 40 },
  { name: 'Endemic Species Richness', unit: 'count', decimals: 0, gaugeMax: 50 },
  { name: 'Endemic Plant Species Richness', unit: 'count', decimals: 0, gaugeMax: 50 },
  { name: 'Flagship Habitat Viability', unit: 'index', decimals: 2, gaugeMax: 1 },
  { name: 'Threatened Species Richness', unit: 'count', decimals: 0, gaugeMax: 50 },
  { name: 'Composite Extinction-Risk Index', unit: 'index', decimals: 2, gaugeMax: 1 },
  { name: 'STAR_T (Threat Abatement)', unit: 'index', decimals: 2, gaugeMax: 1 },
  { name: 'Threatened Plant Species Richness', unit: 'count', decimals: 0, gaugeMax: 50 },
  { name: 'Global Human Modification', unit: 'index', decimals: 2, gaugeMax: 1 },
  { name: 'Light Pollution (VIIRS)', unit: 'index', decimals: 2, gaugeMax: 1 },
  { name: 'Human Disturbance Index', unit: 'index', decimals: 2, gaugeMax: 1 },
  { name: 'Daytime Surface Temperature', unit: 'temp', decimals: 1, gaugeMax: 35 },
  { name: 'Nighttime Surface Temperature', unit: 'temp', decimals: 1, gaugeMax: 35 },
];

// Raw [site_value, tier1_intactness, tier2_intactness] rows, in INDICATOR_META order.
const RAW_ROWS: Record<GundrePlotKey, Array<[number | null, number | null, number | null]>> = {
  'Gundre Plot 1': [
    [100.0, 1.0, 1.0],
    [100.0, 1.0, 1.0],
    [99.5755, 1.0, null],
    [0.0, 1.0, 1.0],
    [0.0, null, null],
    [0.6869, 1.0, 0.9841],
    [3.9001, 0.9455, 1.0],
    [9.541, 0.9643, 0.9569],
    [0.4181, 1.0, 0.7441],
    [0.4689, 1.0, 0.4907],
    [0.817, 1.0, 0.897],
    [0.6036, 1.0, 1.0],
    [0.817, 1.0, 0.897],
    [0.1, 0.9996, 0.9996],
    [0.9888, 0.8386, 0.7984],
    [1.0466, 0.7699, 0.7493],
    [null, null, null],
    [21.0, 0.2763, null],
    [36.0, null, null],
    [0.4303, 0.7676, null],
    [28.0, 1.0, null],
    [0.0426, null, null],
    [0.0207, null, null],
    [13.0, null, null],
    [0.454, 1.0, null],
    [0.4836, 0.9682, null],
    [0.8097, 1.0, null],
    [25.0046, 1.0, null],
    [20.3516, 0.9466, null],
  ],
  'Gundre Plot 2': [
    [100.0, 1.0, 1.0],
    [100.0, 1.0, 1.0],
    [98.9238, 1.0, null],
    [0.0, 1.0, 1.0],
    [7.6763, null, null],
    [0.7038, 1.0, 1.0],
    [4.43, 1.0, 1.0],
    [9.5022, 0.9604, 0.953],
    [0.4421, 1.0, 0.7869],
    [0.4897, 1.0, 0.5126],
    [0.8465, 1.0, 0.9294],
    [0.5582, 0.9558, 0.9862],
    [0.8465, 1.0, 0.9294],
    [0.1, 1.0, 1.0],
    [0.9888, 0.8386, 0.7984],
    [1.2085, 0.8891, 0.8653],
    [null, null, null],
    [21.0, 0.2763, null],
    [36.0, null, null],
    [0.5269, 0.9399, null],
    [28.0, 1.0, null],
    [0.0426, null, null],
    [0.0169, null, null],
    [13.0, null, null],
    [0.3943, 1.0, null],
    [0.4146, 1.0, null],
    [0.6448, 1.0, null],
    [24.8099, 1.0, null],
    [20.1693, 0.9552, null],
  ],
  'Gundre Plot 3': [
    [100.0, 1.0, 1.0],
    [100.0, 1.0, 1.0],
    [99.1097, 1.0, null],
    [0.0, 1.0, 1.0],
    [32.2213, null, null],
    [0.6984, 1.0, 1.0],
    [3.7934, 0.8674, 1.0],
    [9.7185, 0.9823, 0.9747],
    [0.4609, 1.0, 0.8203],
    [0.5141, 1.0, 0.5381],
    [0.8614, 1.0, 0.9458],
    [0.524, 0.8972, 0.9257],
    [0.8614, 1.0, 0.9458],
    [0.1, 1.0, 1.0],
    [0.9888, 0.8283, 0.7984],
    [1.3329, 0.9806, 0.9543],
    [null, null, null],
    [21.0, 0.2763, null],
    [36.0, null, null],
    [0.6056, 1.0, null],
    [28.0, 1.0, null],
    [0.0425, null, null],
    [0.0129, null, null],
    [13.0, null, null],
    [0.3693, 1.0, null],
    [0.3903, 1.0, null],
    [0.4462, 1.0, null],
    [24.5959, 1.0, null],
    [20.0797, 0.9594, null],
  ],
  'Gundre Plot 4': [
    [100.0, 1.0, 1.0],
    [100.0, 1.0, 1.0],
    [99.5159, 1.0, null],
    [0.0217, 1.0, 1.0],
    [86.2581, null, null],
    [0.7228, 1.0, 1.0],
    [3.7575, 0.8591, 1.0],
    [9.8008, 0.9906, 0.983],
    [0.4748, 1.0, 0.845],
    [0.5656, 1.0, 0.592],
    [0.8793, 1.0, 0.9654],
    [0.5103, 0.8738, 0.9016],
    [0.8793, 1.0, 0.9654],
    [0.1, 1.0, 1.0],
    [0.9888, 0.8143, 0.7984],
    [1.2212, 0.8984, 0.8743],
    [null, null, null],
    [22.0, 0.2895, null],
    [36.0, null, null],
    [0.5011, 0.8939, null],
    [28.0, 1.0, null],
    [0.0429, null, null],
    [0.0158, null, null],
    [13.0, null, null],
    [0.3188, 1.0, null],
    [0.3925, 1.0, null],
    [0.0999, 1.0, null],
    [24.4746, 1.0, null],
    [19.987, 0.9639, null],
  ],
};

// Concern levels exactly as shown in the reference baseline scorecard report (client-provided
// screenshots). Only indicators actually visible in that report are populated here — every other
// indicator is left with concern: null rather than guessing at a threshold we can't verify, since
// several of these (e.g. Human Disturbance Index, Global Human Modification) turned out NOT to be
// derivable from the CSV's tier1/tier2 columns (those are ~1.0 for every plot regardless of concern).
const CONCERN_OVERRIDES: Partial<Record<string, Record<GundrePlotKey, ConcernLevel>>> = {
  'Natural Habitat Extent': {
    'Gundre Plot 1': 'very-low', 'Gundre Plot 2': 'very-low', 'Gundre Plot 3': 'very-low', 'Gundre Plot 4': 'very-low',
  },
  'Landscape Connectivity (CPLAND)': {
    'Gundre Plot 1': 'very-low', 'Gundre Plot 2': 'very-low', 'Gundre Plot 3': 'very-low', 'Gundre Plot 4': 'very-low',
  },
  'Vegetation Structure (NDVI)': {
    'Gundre Plot 1': 'very-low', 'Gundre Plot 2': 'very-low', 'Gundre Plot 3': 'very-low', 'Gundre Plot 4': 'very-low',
  },
  'Habitat Health Index (HHI)': {
    'Gundre Plot 1': 'very-low', 'Gundre Plot 2': 'very-low', 'Gundre Plot 3': 'very-low', 'Gundre Plot 4': 'very-low',
  },
  'Ecosystem Integrity Index': {
    'Gundre Plot 1': 'low', 'Gundre Plot 2': 'low', 'Gundre Plot 3': 'low', 'Gundre Plot 4': 'low',
  },
  'Biodiversity Intactness Index': {
    'Gundre Plot 1': 'very-low', 'Gundre Plot 2': 'very-low', 'Gundre Plot 3': 'very-low', 'Gundre Plot 4': 'very-low',
  },
  'Endemic Species Richness': {
    'Gundre Plot 1': 'very-high', 'Gundre Plot 2': 'very-high', 'Gundre Plot 3': 'very-high', 'Gundre Plot 4': 'very-high',
  },
  'Global Human Modification': {
    'Gundre Plot 1': 'moderate', 'Gundre Plot 2': 'moderate', 'Gundre Plot 3': 'moderate', 'Gundre Plot 4': 'moderate',
  },
  'Human Disturbance Index': {
    'Gundre Plot 1': 'very-high', 'Gundre Plot 2': 'moderate', 'Gundre Plot 3': 'very-low', 'Gundre Plot 4': 'very-low',
  },
};

const buildIndicators = (
  plotKey: GundrePlotKey,
  rows: Array<[number | null, number | null, number | null]>,
): ScorecardIndicator[] =>
  INDICATOR_META.map((meta, i) => ({
    name: meta.name,
    unit: meta.unit,
    decimals: meta.decimals,
    gaugeMax: meta.gaugeMax,
    value: rows[i][0],
    tier1: rows[i][1],
    tier2: rows[i][2],
    concern: CONCERN_OVERRIDES[meta.name]?.[plotKey] ?? null,
  }));

export const gundreScorecards: Record<GundrePlotKey, ScorecardIndicator[]> = {
  'Gundre Plot 1': buildIndicators('Gundre Plot 1', RAW_ROWS['Gundre Plot 1']),
  'Gundre Plot 2': buildIndicators('Gundre Plot 2', RAW_ROWS['Gundre Plot 2']),
  'Gundre Plot 3': buildIndicators('Gundre Plot 3', RAW_ROWS['Gundre Plot 3']),
  'Gundre Plot 4': buildIndicators('Gundre Plot 4', RAW_ROWS['Gundre Plot 4']),
};

export const getIndicator = (
  indicators: ScorecardIndicator[] | null | undefined,
  name: string,
): ScorecardIndicator | null => indicators?.find((i) => i.name === name) ?? null;

export const CONCERN_COLORS: Record<ConcernLevel, string> = {
  'very-low': '#2dd4bf',
  low: '#22c55e',
  moderate: '#eab308',
  high: '#f97316',
  'very-high': '#ef4444',
};

export const CONCERN_LABELS: Record<ConcernLevel, string> = {
  'very-low': 'Very Low Concern',
  low: 'Low Concern',
  moderate: 'Moderate Concern',
  high: 'High Concern',
  'very-high': 'Very High Concern',
};

export const formatIndicatorValue = (indicator: ScorecardIndicator | null | undefined): string => {
  if (!indicator || indicator.value === null) return '--';
  const rounded = Number(indicator.value.toFixed(indicator.decimals));
  switch (indicator.unit) {
    case 'percent':
      return `${rounded}%`;
    case 'temp':
      return `${rounded}°C`;
    case 'count':
      return `${rounded}`;
    case 'index':
    default:
      return `${rounded}`;
  }
};

// Resolves a site name like "Gundre Plot 3" (any casing/spacing) to its scorecard key.
export const resolveGundrePlotKey = (siteName: string | null | undefined): GundrePlotKey | null => {
  if (!siteName) return null;
  const match = siteName.match(/plot\s*([1-4])/i);
  if (!match) return null;
  return `Gundre Plot ${match[1]}` as GundrePlotKey;
};

export const NATURE_ATLAS_SECTIONS: Array<{ title: string; indicatorNames: string[] }> = [
  {
    title: 'Habitat & Landscape',
    indicatorNames: [
      'Natural Habitat Extent',
      'Natural Land Cover Proportion',
      'Landscape Connectivity (CPLAND)',
      'Tree Cover Loss Rate',
      'KBA/IBA Overlap',
      'Vegetation Structure (NDVI)',
      'Habitat Health Index (HHI)',
      'Forest Landscape Integrity Index',
    ],
  },
  {
    title: 'Ecosystem Integrity',
    indicatorNames: [
      'Ecosystem Integrity Index',
      'EII: Structural Integrity',
      'EII: Compositional Integrity',
      'EII: Functional Integrity',
      'Biodiversity Intactness Index',
      'Potentially Disappeared Fraction',
      'Aridity Index',
      'Leaf Area Index (MODIS MCD15A3H)',
      'Canopy Height Model (GEDI L2A rh98)',
    ],
  },
  {
    title: 'Species & Habitat Viability',
    indicatorNames: [
      'Endemic Species Richness',
      'Endemic Plant Species Richness',
      'Flagship Habitat Viability',
      'STAR_T (Threat Abatement)',
    ],
  },
  {
    title: 'Human Pressure & Climate',
    indicatorNames: [
      'Global Human Modification',
      'Light Pollution (VIIRS)',
      'Human Disturbance Index',
      'Daytime Surface Temperature',
      'Nighttime Surface Temperature',
    ],
  },
];

// Indicators used to surface the single most pressing hazard on the Overview/Climate tabs.
const HAZARD_CANDIDATES = [
  'Human Disturbance Index',
  'Global Human Modification',
  'Light Pollution (VIIRS)',
  'Tree Cover Loss Rate',
];

export const getMostProminentHazard = (
  indicators: ScorecardIndicator[] | null | undefined,
): ScorecardIndicator | null => {
  if (!indicators) return null;
  let best: ScorecardIndicator | null = null;
  HAZARD_CANDIDATES.forEach((name) => {
    const ind = getIndicator(indicators, name);
    if (ind && ind.value !== null && (best === null || (ind.value as number) > (best.value as number))) {
      best = ind;
    }
  });
  return best;
};
